/**
 * Version d'interface décidée par parc, par un membre AKONGO.
 *
 * Trois régimes, écrits dans `zoo/{zooId}.interfacePolicy` :
 * - `v1`     : le parc reste sur l'ancienne interface. C'est le régime par
 *              défaut de tous les parcs existants — un champ absent ne change
 *              rien à ce qu'ils voyaient hier.
 * - `choice` : le parc est en migration. Ses membres reçoivent la V2 et le
 *              sélecteur de version : ils passent de l'une à l'autre.
 * - `v2`     : le parc ne connaît que la nouvelle interface. Pas de
 *              sélecteur, et **pas de fenêtre « Bienvenue dans votre nouvelle
 *              interface »** : rien n'a changé pour ces personnes, elles n'ont
 *              jamais vu l'ancienne. Leur point d'entrée est « Bien démarrer ».
 *
 * L'application installée impose la V2, sans sélecteur, pour tous les profils.
 * Dans un onglet navigateur, le staff AKONGO garde la V2 et le sélecteur, quel que soit le
 * régime du parc : c'est lui qui règle ce champ et qui dépanne les parcs.
 *
 * Dans un onglet navigateur, une vue sans équivalent V2 continue de s'ouvrir en V1, même dans un parc
 * `v2` : la bascule se fait vue par vue au fil de la parité, et la personne
 * ne doit jamais se retrouver devant une porte fermée. L'app installée
 * reste dans une entrée V2 si aucune restauration n'est possible.
 */

import { isStandaloneApp } from "../../pwa/browserDisplayMode";

export const INTERFACE_POLICIES = Object.freeze(["v1", "choice", "v2"]);
export const DEFAULT_INTERFACE_POLICY = "v1";

/** Régime d'un parc, tel qu'il est lisible dans son document. */
export const readInterfacePolicy = (zooOptions) => {
  const value = typeof zooOptions?.interfacePolicy === "string" ? zooOptions.interfacePolicy.trim() : "";
  return INTERFACE_POLICIES.includes(value) ? value : DEFAULT_INTERFACE_POLICY;
};

/** Le parc ouvre-t-il la V2 à ses membres ? */
export const parkOpensV2 = (policy) => policy === "choice" || policy === "v2";

/** Le parc impose-t-il la V2 ? (le sélecteur disparaît, la V2 devient l'entrée) */
export const parkForcesV2 = (policy) => policy === "v2";

/**
 * Sélecteur de version : proposé quand le parc laisse le choix, et toujours
 * au staff AKONGO — sans quoi il ne pourrait plus rejoindre la V1 d'un parc
 * qu'il vient de passer en V2.
 */
export const offersVersionSwitch = ({ policy, staff = false, standalone = isStandaloneApp() }) => (
  !standalone && (staff === true || policy === "choice")
);

/**
 * Fenêtre d'accueil « Bienvenue dans votre nouvelle interface » : elle
 * raconte un changement. Elle n'a de sens que pendant la migration.
 */
export const showsInterfaceMigrationDialog = (policy) => policy === "choice";

/**
 * Fenêtre « Bienvenue sur Animal Welfare Monitor » : l'accueil d'une personne qui n'a connu
 * que la V2. Pas de « ce qui change », mais le choix du thème, quelques
 * astuces et le chemin vers « Bien démarrer ». Elle ne concerne que les parcs
 * en V2 seule ; un parc en migration a sa propre fenêtre.
 */
export const showsInterfaceWelcomeDialog = (policy) => policy === "v2";

/**
 * Version imposée à une personne, ou `null` si elle reste libre de son
 * réglage. L'application installée impose la V2, y compris au staff.
 */
export const forcedInterfaceVersion = ({ policy, staff = false, standalone = isStandaloneApp() }) => (
  standalone || (!staff && parkForcesV2(policy)) ? "v2" : null
);

/**
 * Préférences de version d'une personne dont le parc a tranché : la lecture
 * répond la version imposée, l'écriture reste acceptée sans effet (le
 * réglage « Version de l'interface » du compte n'a plus de prise, mais il ne
 * doit pas lever d'erreur). Sans version imposée, rien n'est intercepté.
 */
export const withForcedVersion = (preferences, version) => (
  version ? Object.freeze({ read: () => version, write: () => {} }) : preferences
);
